import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Status_Enum,
  UpdateAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * tRPC input type for updateAssessment.
 * Assessment has no schedule, so no schedule field transformation needed.
 */
type UpdateAssessmentTRPCInput = {
  Id: string;
  Title: string;
  Status: Assessment_Status_Enum;
  OriginatingItemId?: string | null;
  Summary?: string | null;
  ActualCompletionDate?: string | null;
  NextTestDate?: string | null;
  StartDate?: string | null;
  TargetCompletionDate?: string | null;
  CompletedByUser?: string | null;
  Outcome?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (_trpcData: {
  Id: string;
}): UpdateAssessmentMutation => {
  return {
    updateAssessmentApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  };
};

export const useUpdateAssessmentTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.getById.queryKey(),
        });
        // COI-471 parity: Apollo twin evicts the assessment-result fields
        // (result ratings roll up into the assessment).
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.documentAssessmentResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.obligationAssessmentResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.riskAssessmentResult.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateAssessment: async (
      variables: UpdateAssessmentTRPCInput
    ): Promise<UpdateAssessmentMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
